import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { DndContext } from '@dnd-kit/core'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import {
    BUTTON_TYPE,
    DIVIDER_SIZE,
    HEADING_TYPE,
    TEXT_SIZES,
} from '@helpers/constants'
import { getImageSrc, shuffleArray } from '@helpers/utilities'
import { WordList } from './word-list'
import { Droppable } from './droppable'
import ExerciseBlock from '@components/ExerciseBlock'
import ExerciseHeader from '@components/ExerciseHeader'
import { useResponsive } from '@hooks/useResponsive'
import Button from '@components/Button'

const DragWordToImage = (props) => {
    if (!props.wordPairs) return null

    const {windowSize: {width}} = useResponsive()
    const { name,
        preamble,
        image,
        showPanel,
        onShowPanel,
        onClosePanel,
    } = props
    const [wordPairs, setWordPairs] = useState(shuffleArray(props.wordPairs))
    const [answers, setAnswers] = useState(props.wordPairs.map(
        wordPair => ({
            wordPair,
            isCorrect: false,
            selectedWordPair: undefined
        })
    ))
    const [isDragging, setIsDragging] = useState(false)
    const [wordHeight, setWordHeight] = useState(undefined)
    const [draggingFrom, setDraggingFrom] = useState(false)
    const [draggingId, setDraggingId] = useState(undefined)
    const [showSuccess, setShowSuccess] = useState(false)

    useEffect(() => {
        if (answers.every((answer) => answer.isCorrect)) {
            setShowSuccess(true)
        }
    }, [answers])

    useEffect(() => {
        setWordPairs(shuffleArray(props.wordPairs))
        setAnswers(props.wordPairs.map(
            wordPair => ({
                wordPair,
                isCorrect: false,
                selectedWordPair: undefined
            })
        ))
    }, [props.wordPairs])

    const reset = () => {
        setShowSuccess(false)
        setWordPairs(shuffleArray(props.wordPairs))
        setAnswers(props.wordPairs.map(
            wordPair => ({
                wordPair,
                isCorrect: false,
                selectedWordPair: undefined
            })
        ))
    }

    const move = (sourceDroppableId, destinationDroppableId, draggableId) => {
        const draggableIndex = draggableId.split('-')[1]
        const sourceDroppableIndex = sourceDroppableId.split('-')[1]

        if (destinationDroppableId === 'WordList') {
            const newWordPairs = Array.from(wordPairs)
            newWordPairs.splice(wordPairs.length, 0, answers[sourceDroppableIndex].selectedWordPair)
            setWordPairs(newWordPairs)
            answers[sourceDroppableIndex].selectedWordPair = undefined
        } else if (destinationDroppableId !== 'WordList' && sourceDroppableId !== 'WordList') {
            const destinationIndex = destinationDroppableId.split('-')[1]
            const destinationWordPair = answers[destinationIndex].selectedWordPair
            answers[destinationIndex].selectedWordPair = answers[sourceDroppableIndex].selectedWordPair
            answers[sourceDroppableIndex].selectedWordPair = destinationWordPair
        } else if (destinationDroppableId !== 'WordList' && sourceDroppableId === 'WordList') {
            const destinationIndex = destinationDroppableId.split('-')[1]
            const tempWord = answers?.[destinationIndex]?.selectedWordPair
            answers[destinationIndex].selectedWordPair = wordPairs[draggableIndex]
            wordPairs.splice(draggableIndex, 1)
            if (tempWord) {
                wordPairs.push(tempWord)
            }
            setWordPairs(wordPairs)
        }

        setAnswers(answers.map((answer) => {
            const {wordPair, selectedWordPair} = answer
            answer.isCorrect = selectedWordPair && wordPair.word === selectedWordPair.word
            return answer
        }))
    }

    const onDragEnd = (result) => {
        setIsDragging(false)
        setDraggingId(undefined)

        const { active, over } = result

        if (!over) {
           return
        }
        const sourceDroppableId = active.data.current.fromDroppableId
        const destinationDroppableId = over.id

        if (sourceDroppableId !== 'WordList' || destinationDroppableId !== 'WordList') {
            move(sourceDroppableId, destinationDroppableId, active.id)
        }
    }

    return (
        <>
            <ExerciseBlock
                name={name}
                onShowPanel={onShowPanel}
                preamble={preamble}
                image={image}
            >
                Starta övning
            </ExerciseBlock>
            <ExerciseHeader
                exerciseName={name}
                closePanel={() => {
                    onClosePanel()
                }}
                openPanel={showPanel}
                width={width <= 1024 ? '50%' : null}
            >
                {showSuccess &&
                    <div className={'flex flex-col items-center mt-4'}>
                        <Heading size={TEXT_SIZES.XXLARGE} headingLevel={HEADING_TYPE.H1}>Bra jobbat!</Heading>
                        <Divider size={DIVIDER_SIZE.SMALL} />
                        <p>Alla rätt!</p>
                        <Divider size={DIVIDER_SIZE.SMALL} />

                        <div className={'flex'}>
                            <Button
                                handleClick={() => reset()}
                                type={BUTTON_TYPE.SECONDARY}
                                margin
                            >
                                Gör om
                            </Button>
                            <Button
                                handleClick={onClosePanel}
                                type={BUTTON_TYPE.PRIMARY}
                                margin
                            >
                                Okej
                            </Button>
                        </div>
                        <Divider size={DIVIDER_SIZE.SMALL} />
                    </div>
                }

                <DndContext
                    autoScroll={false}
                    onDragStart={(data) => {
                        setDraggingId(data.active.id)
                        setDraggingFrom(data.active.data.current.fromDroppableId)
                        setIsDragging(true)}
                    }
                    onDragEnd={onDragEnd}
                >
                    <div className={'grid grid-cols-3 mt-8 mr-[-16px]'}>
                        {answers.map((item, index) =>
                            <div className="flex flex-shrink-0 flex-col justify-between mr-4 mb-8" key={index}>
                                <div className={'relative pb-[100%] overflow-hidden border border-mediumGrey border-b-0 rounded rounded-b-none'}>
                                    <img className='absolute object-cover h-full w-full' src={getImageSrc(item?.wordPair?.image)} />
                                </div>
                                <Droppable
                                    droppableId={'droppable-' + index}
                                    selectedWordPair={item.selectedWordPair}
                                    correctWord={item.wordPair.word}
                                    index={index + wordPairs.length}
                                    isDragging={isDragging}
                                    disabled={draggingFrom === 'droppable-' + index}
                                    draggingId={draggingId}
                                    wordHeight={wordHeight}
                                />
                            </div>
                        )}
                    </div>

                    <WordList
                        items={wordPairs}
                        totalWordCount={props.wordPairs.length}
                        draggingId={draggingId}
                        ref={(ref) => {
                            if (!ref || wordHeight !== undefined) {
                                return
                            }
                            const children = Array.from(ref.children)

                            setWordHeight(children.reduce(
                                (max, element) => {
                                    return element.clientHeight > max
                                        ? element.clientHeight
                                        : max
                                }, 0)
                            )
                        }}
                    />
                </DndContext>
            </ExerciseHeader>
        </>
    )
}

DragWordToImage.propTypes = {
    name: PropTypes.string,
    wordPairs: PropTypes.array,
}

export default DragWordToImage
